import * as z from "zod";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/Inputs/TextInput";
import { SelectInput } from "@/components/Inputs/SelectInput";
import { UnitSelect } from "@/components/Inputs/UnitSelect";
import { SchemaSalida, type SchemaFlujoEntidadIn } from "./Flujo.schema";
import {
	SchemaRawCO2,
	SchemaProducto,
	SchemaResiduo,
} from "./Salida.schema";

interface FormProps<T> {
	readonly value: z.input<T>;
	readonly onChange: (d: SchemaFlujoEntidadIn) => void;
	readonly onDelete: (d: SchemaFlujoEntidadIn) => void;
	readonly errors: { [key in keyof z.input<T>]?: string[] };
}
export function FormProyectoSalida(props: FormProps<typeof SchemaSalida>) {
	const { value, onChange, errors } = props;
	const handler: ChangeHandler<typeof SchemaSalida> = (field, val) => {
		onChange({ ...value, [field]: val });
	};

	const handleDelete = () => {
		const message = `¿Está seguro de que desea eliminar la salida "${value.nombre}"?`;
		if (window.confirm(message)) {
			props.onDelete(value);
		}
	};

	return (
		<form onSubmit={(e) => e.preventDefault()} className="space-y-4">
			<TextInput
				value={value.nombre}
				onChange={(e) => handler("nombre", e.target.value)}
				errors={errors.nombre}
				label="Nombre"
				placeholder="Nombre"
				type="text"
			/>
			{value.subtipo != "main_producto" && (
				<SelectInput
					value={value.subtipo}
					onChange={(e) => handler("subtipo", e as "raw_co2" | "residuo" | "producto")}
					errors={errors.subtipo}
					label="Tipo"
					// placeholder="Tipo"
					options={{
						raw_co2: "CO2 Directo",
						Residuo: "Residuo",
						Producto: "Producto",
					}}
				/>
			)}

			{value.subtipo == "raw_co2" && (
				<RawCO2Fields value={value} handler={handler} errors={errors} />
			)}
			{value.subtipo == "residuo" && (
				<ResiduoFields value={value} handler={handler} errors={errors} />
			)}
			{value.subtipo == "producto" && (
				<ProductoFields value={value} handler={handler} errors={errors} />
			)}
			{value.subtipo == "main_producto" && (
				<MainProductoFields value={value} handler={handler} errors={errors} />
			)}

			{value.subtipo != "main_producto" && (
				<Button variant="destructive" onClick={handleDelete} className="w-full">Eliminar</Button>
			)}
		</form>
	);
}

interface SubFormProps<T> {
	readonly value: z.input<T>;
	readonly handler: ChangeHandler<T>;
	readonly errors: z.core.$ZodFlattenedError<z.input<T>>["fieldErrors"];
}
export function RawCO2Fields(props: SubFormProps<typeof SchemaRawCO2>) {
	const { value, handler, errors } = props;

	return (
		<TextInput
			value={String(value.cantidad)}
			onChange={(e) => handler("cantidad", e.target.value)}
			errors={errors.cantidad}
			label="Cantidad"
			placeholder="Cantidad"
			type="text"
		/>
	);
}
export function ResiduoFields(props: SubFormProps<typeof SchemaResiduo>) {
	const { value, handler, errors } = props;

	const onChangeUnit: Setter<string> = (valOrFunc) => {
		const val = typeof valOrFunc === "function" ? valOrFunc(value.unidad) : valOrFunc;
		handler("unidad", val);
	};

	return (
		<>
			<UnitSelect
				value={value.unidad}
				onChange={onChangeUnit}
				errors={errors.unidad}
				label="Unidad"
				placeholder="Unidad"
			/>
			<TextInput
				value={String(value.cantidad)}
				onChange={(e) => handler("cantidad", e.target.value)}
				errors={errors.cantidad}
				label="Cantidad"
				placeholder="Cantidad"
				type="text"
			/>
			<TextInput
				value={String(value.equalCO2)}
				onChange={(e) => handler("equalCO2", e.target.value)}
				errors={errors.equalCO2}
				label="Gramos de CO2 por unidad"
				placeholder="CO2 por unidad"
				type="text"
			/>
		</>
	);
}
export function ProductoFields(props: SubFormProps<typeof SchemaProducto>) {
	const { value, handler, errors } = props;
	const onChangeUnit: Setter<string> = (valOrFunc) => {
		const val = typeof valOrFunc === "function" ? valOrFunc(value.unidad) : valOrFunc;
		handler("unidad", val);
	};

	return (
		<>
			<UnitSelect
				value={value.unidad}
				onChange={onChangeUnit}
				errors={errors.unidad}
				label="Unidad"
				placeholder="Unidad"
			/>

			<TextInput
				value={String(value.cantidad)}
				onChange={(e) => handler("cantidad", e.target.value)}
				errors={errors.cantidad}
				label="Cantidad"
				placeholder="Cantidad"
				type="text"
			/>

			<Button variant="secondary">Ver proceso</Button>
		</>
	);
}
export function MainProductoFields(props: SubFormProps<typeof SchemaProducto>) {
	const { value, handler, errors } = props;

	const onChangeUnit: Setter<string> = (valOrFunc) => {
		const val = typeof valOrFunc === "function" ? valOrFunc(value.unidad) : valOrFunc;
		handler("unidad", val);
	};

	return (
		<>
			<UnitSelect
				value={value.unidad}
				onChange={onChangeUnit}
				errors={errors.unidad}
				label="Unidad"
				placeholder="Unidad"
			/>

			<TextInput
				value={String(value.cantidad)}
				onChange={(e) => handler("cantidad", e.target.value)}
				errors={errors.cantidad}
				label="Cantidad"
				placeholder="Cantidad"
				type="text"
			/>
		</>
	);
}

type ChangeHandler<T> = <K extends keyof z.input<T>>(field: K, val: z.input<T>[K]) => void;
